/**
 * Per-task model tiering — the Phase-1 cost lever from
 * `docs/architecture/sed-report-cost-strategy.md` (§4 model levers, §7 Phase 1).
 *
 * The biggest single avoidable spend is running HIGH-FAN-OUT, LOW-JUDGMENT tasks
 * (per-doc extraction, doc↔checklist relevance scoring, evidence-conflict
 * comparison, OCR/image description) on the premium Pro model. Each of those is
 * structured field-pull or transcription — not narrative judgment — so Flash is
 * the right tier and Pro is a ~4x overspend (Pro $1.25/$10 vs Flash $0.30/$2.50).
 *
 * SYNTHESIS tasks (narrative Stage 2 `generateReportContent`, evidence Stage 1
 * `analyzeEvidence`, DAM `synthesize`/`verify`) DELIBERATELY stay on Pro — Flash
 * narrative drifts, which costs MORE in extra review iterations and lowers truth.
 * Those callers simply never ask this module for a tier.
 *
 * REVERSIBILITY (acceptance criterion): with the flag OFF — the default — every
 * task resolves to {@link GEMINI_PRO_MODEL}, i.e. byte-identical to today's
 * behaviour (everything back on Pro). The flag flip is a Cloud Run env change,
 * zero redeploy, matching the `geminiModels.ts` rollback story.
 */
import { GEMINI_FLASH_MODEL, GEMINI_PRO_MODEL } from './geminiModels';

/**
 * Tasks eligible for down-tiering to Flash. These are the §1 cost-map sinks
 * #1/#3/#5 — fan-out-heavy, judgment-light. The narrative/synthesis stages are
 * intentionally NOT members of this union: they never route through here.
 */
export type TieredTask =
  | 'extraction' // extractFromDocument — per-(doc×section) structured field pull (#1)
  | 'docMatching' // relevanceScoring — per-(doc×checklist-item) relevance (#3)
  | 'evidenceConflict' // evidenceConflict — per-(section×doc) scalar comparison (#3)
  | 'ocr'; // describeImageForSite / extractPdfTextForSite fallback — transcription (#5)

const TIERING_FLAG = 'VITE_ENABLE_MODEL_TIERING';
const TIERING_FLAG_SERVER = 'ENABLE_MODEL_TIERING';

/**
 * Read a boolean env flag with the case-insensitive `'true'`/`'1'` rule and the
 * `import.meta.env` → `process.env` precedence used elsewhere in the repo
 * (mirrors `runtimeFlags.ts`/`tracedGeminiCall.ts`). Browser bundle reads the
 * `VITE_`-prefixed var; Node/server/tests read the bare var.
 */
function readEnvFlag(name: string): string | undefined {
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as { env?: unknown }).env != null) {
      const v = ((import.meta as { env: Record<string, unknown> }).env)[name];
      if (typeof v === 'string') return v;
    }
  } catch {
    /* import.meta unavailable in older Node — fall through to process.env */
  }
  if (typeof process !== 'undefined' && process.env) {
    return process.env[name];
  }
  return undefined;
}

function isTruthy(raw: string | undefined): boolean {
  if (typeof raw !== 'string') return false;
  const v = raw.trim().toLowerCase();
  return v === 'true' || v === '1';
}

/**
 * Whether per-task model tiering is enabled. Default OFF in EVERY environment —
 * unlike most rollout flags there is no dev/prod asymmetry, so flag-off is
 * unconditionally today's all-Pro behaviour. Enable via either the Vite-bundle
 * flag (browser) or the bare server flag.
 */
export function isModelTieringEnabled(): boolean {
  return isTruthy(readEnvFlag(TIERING_FLAG)) || isTruthy(readEnvFlag(TIERING_FLAG_SERVER));
}

/**
 * Resolve the model id for a tier-eligible task.
 *
 * Flag OFF (default) ⇒ {@link GEMINI_PRO_MODEL} for ALL tasks (no behaviour
 * change). Flag ON ⇒ {@link GEMINI_FLASH_MODEL} for the down-tiered tasks.
 *
 * Both models honour their `GEMINI_*_MODEL` env overrides, so the actual ids
 * track whatever Vertex access the project has.
 */
export function tieredModelFor(task: TieredTask): string {
  if (!isModelTieringEnabled()) return GEMINI_PRO_MODEL;
  switch (task) {
    case 'extraction':
    case 'docMatching':
    case 'evidenceConflict':
    case 'ocr':
      return GEMINI_FLASH_MODEL;
    default: {
      // Exhaustiveness guard — a new TieredTask must declare its routing.
      const _never: never = task;
      return GEMINI_PRO_MODEL;
    }
  }
}
